import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'

export interface PercentVisualizationProps {
  /**
   * The numeric value to display
   */
  value: string | number
  /**
   * Optional raw metric value supplied alongside the normalized score
   */
  rawValue?: number
  /**
   * Title displayed above the number
   */
  title: string
  /**
   * Description text displayed next to the number
   */
  description: string
  /**
   * Variant for team or individual styling
   */
  variant?: 'team' | 'individual'
  /**
   * Optional metric name for additional context
   */
  metricName?: string
}

/**
 * PercentVisualization - Displays a large gradient number with a % sign and title/description
 *
 * Features:
 * - Gradient background containers
 * - Large gradient number with % sign and glow effect
 * - Title and description layout
 * - Horizontal number + description arrangement
 */
const PercentVisualization: React.FC<PercentVisualizationProps> = ({
  value,
  title,
  description,
  variant: _variant // Reserved for future background color variations
}) => {
  return (
    <Box
      sx={{
        // Outer container - 351px x 158px
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',

        width: '100%',
        height: '100%',

        flex: 'none'
      }}
    >
      {/* Inner frame - 330px x 138px */}
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px',
          gap: '24px',
          isolation: 'isolate',
          width: '100%',
          height: '100%',
          background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
          backgroundBlendMode: 'soft-light, normal',
          border: `1px solid ${INTERAX_COLORS.white04}`,
          boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
          borderRadius: '10px',
          flex: 'none'
        }}
      >
        {/* Title */}
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.headline5,
            color: INTERAX_COLORS.white,
            alignSelf: 'stretch'
          }}
        >
          {title}
        </Typography>

        {/* Number and Description Row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0px',
            gap: '8px',
            width: '100%',
            height: '40px'
          }}
        >
          {/* Gradient Number with % sign */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flex: 'none'
            }}
          >
            <Typography
              sx={{
                boxSizing: 'border-box',
                fontFamily: 'Lexend Deca',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '48px',
                lineHeight: '36px',
                display: 'flex',
                alignItems: 'flex-start',
                background: 'linear-gradient(360deg, #FFD196 0%, #FFAB40 23.08%, #FFFFFF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',

                textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
                flex: 'none'
              }}
            >
              {value}
            </Typography>
            <Typography
              sx={{
                boxSizing: 'border-box',
                fontFamily: 'Lexend Deca',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '48px',
                lineHeight: '36px',
                display: 'flex',
                alignItems: 'flex-start',
                background: 'linear-gradient(360deg, #FFD196 0%, #FFAB40 23.08%, #FFFFFF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',

                textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
                flex: 'none'
              }}
            >
              %
            </Typography>
          </Box>

          {/* Description */}

          {description !== 'count' && (
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.thunderGrey,
                display: 'flex',
                alignItems: 'center',
                flex: 'none'
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default PercentVisualization
